import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import { Currency, FX_TO_USD, priceCentsToUsdCents } from '@etiquetachef/types';
import { formatCurrencyCents, formatUsdCents } from './formatters';
import type { FinanceMetrics } from './useFinanceMetrics';

const ROWS: Currency[] = [Currency.BRL, Currency.USD, Currency.EUR];

/**
 * Tabela "Receita por moeda" do `/dashboard/finance`: para BRL/USD/EUR, o câmbio fixo para USD,
 * o MRR médio na moeda nativa e o equivalente em USD.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200 (rota /dashboard/finance).
 *
 * Contrato: só apresentação de `FinanceMetrics.mrrByCurrencyAvgCents` (CENTAVOS). O USD eq. é
 * recalculado aqui com `priceCentsToUsdCents` sobre a média já agregada — mesma tabela
 * `FX_TO_USD` (`packages/types/src/billing.ts`) que o agregador usa, então não diverge do KPI.
 * `ROWS` é fixo: moeda nova em `Currency` exige linha aqui (e formatter em `formatters.ts`).
 *
 * Usado por: `FinanceDashboardPage` (web `apps/admin`, super-admin).
 *
 * Dicas: `FX_TO_USD` é câmbio FIXO em código, não cotação do dia — a coluna "FX" existe para
 * deixar isso explícito a quem lê o painel.
 *
 * Histórico:
 *  - 2026-06-09 00:37 +0200 — (39-G) migração para Cloudscape `Table`.
 */
export function RevenueByCurrencyTable({ metrics }: { metrics: FinanceMetrics }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Table<Currency>
      items={ROWS}
      trackBy={(c): string => c}
      variant="container"
      header={<Header variant="h3">{t('finance.tables.revenueByCurrency.title')}</Header>}
      columnDefinitions={[
        {
          id: 'currency',
          header: t('finance.tables.revenueByCurrency.currency'),
          cell: (c): React.ReactNode => c,
        },
        {
          id: 'fx',
          header: t('finance.tables.revenueByCurrency.fx'),
          cell: (c): React.ReactNode => FX_TO_USD[c].toFixed(2),
        },
        {
          id: 'native',
          header: t('finance.tables.revenueByCurrency.native'),
          cell: (c): React.ReactNode => formatCurrencyCents(metrics.mrrByCurrencyAvgCents[c], c),
        },
        {
          id: 'usdEq',
          header: t('finance.tables.revenueByCurrency.usdEq'),
          cell: (c): React.ReactNode =>
            formatUsdCents(priceCentsToUsdCents(metrics.mrrByCurrencyAvgCents[c], c)),
        },
      ]}
    />
  );
}
